import { audio } from '../audio/sfx.ts';
import { t } from '../i18n.ts';
import { saveSettings, settings } from '../settings.ts';
import { h } from './dom.ts';
import { ICONS } from './icons.ts';

/**
 * The sound button, in the bastion's actions and the battle's top bar: it opens a small panel with music
 * and effects, each with its switch and its volume, without going into the settings.
 */
export const musicToggle = (wide = false): HTMLElement => {
  const ico = h('span');
  const lbl = wide ? h('span.lbl', {}, t('settings.music')) : null;
  const btn = h(`button.iconbtn.music-toggle${wide ? '.wide' : ''}`, { 'data-test': 'music', 'aria-label': t('settings.audio'), title: t('settings.audio'), 'aria-haspopup': 'dialog' }, ico, lbl);
  const paint = (): void => {
    ico.innerHTML = settings.music ? ICONS.music : ICONS.musicOff;
    btn.classList.toggle('off', !settings.music);
  };
  let panel: HTMLElement | null = null;
  // While the panel is open: the button can leave the page with its screen, and the panel goes with it.
  let gone: MutationObserver | null = null;
  const close = (): void => {
    panel?.remove();
    panel = null;
    gone?.disconnect();
    gone = null;
    btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', outside, true);
  };
  const outside = (e: Event): void => {
    if (!panel?.contains(e.target as Node) && !btn.contains(e.target as Node)) close();
  };
  const row = (label: string, on: () => boolean, vol: () => number, set: (on: boolean, vol: number) => void, test: string): HTMLElement => {
    const sw = h('input', { type: 'checkbox', 'data-test': `${test}-on`, 'aria-label': label }) as HTMLInputElement;
    const range = h('input.vol', { type: 'range', min: 0, max: 100, step: 5, 'data-test': `${test}-vol`, 'aria-label': `${label} · ${t('settings.volume')}` }) as HTMLInputElement;
    const sync = (): void => {
      sw.checked = on();
      range.value = String(Math.round(vol() * 100));
      range.disabled = !on();
    };
    sw.addEventListener('change', () => {
      set(sw.checked, vol());
      sync();
      paint();
    });
    range.addEventListener('input', () => set(true, Number(range.value) / 100));
    // A step of the slider plays the click, so effects can be heard at the new level.
    range.addEventListener('change', () => audio.play('click'));
    sync();
    return h('div.snd-row', {}, h('label.snd-head', {}, h('span', {}, label), sw), range);
  };
  const open = (): void => {
    panel = h(
      'div.snd-panel',
      { role: 'dialog', 'aria-label': t('settings.audio'), 'data-test': 'sound-panel' },
      row(t('settings.music'), () => settings.music, () => settings.musicVol, (on, vol) => {
        settings.music = on;
        settings.musicVol = vol;
        saveSettings();
        audio.setMusic(on, vol);
      }, 'music'),
      row(t('settings.sfx'), () => settings.sound, () => settings.sfxVol, (on, vol) => {
        settings.sound = on;
        settings.sfxVol = vol;
        saveSettings();
        audio.setSfx(on, vol);
      }, 'sfx'),
    );
    // Keys here move the sliders, never the camera.
    panel.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') close();
    });
    panel.addEventListener('pointerdown', (e) => e.stopPropagation());
    document.body.append(panel);
    // Next to the button, opening toward the free side of the screen and kept inside it.
    const r = btn.getBoundingClientRect();
    const w = panel.offsetWidth;
    const ph = panel.offsetHeight;
    const x = Math.max(8, Math.min(window.innerWidth - w - 8, r.right - w));
    const below = r.top + r.height / 2 < window.innerHeight / 2;
    panel.style.left = `${x}px`;
    panel.style.top = `${below ? r.bottom + 8 : Math.max(8, r.top - ph - 8)}px`;
    btn.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', outside, true);
    gone = new MutationObserver(() => {
      if (!btn.isConnected) close();
    });
    gone.observe(document.body, { childList: true, subtree: true });
  };
  btn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    audio.play('click');
    if (panel) close();
    else open();
  });
  paint();
  return btn;
};
